import { Check, ChevronsUpDown, Plus } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { Badge, Popover, PopoverContent, PopoverTrigger } from '@/components/ui'
import { CommunityIcon } from '@/components/common/CommunityIcon'
import { cn } from '@/components/ui/cn'
import { useHomeCommunities } from '@/features/communities/api'
import { useAuth } from '@/lib/auth'
import { t } from '@/lib/i18n'
import { joinableCommunities, switcherCommunities } from './shell-utils'

const rowClass =
  'flex h-12 w-full items-center gap-3 rounded-lg px-2.5 text-left text-body text-text outline-none transition-colors duration-100 hover:bg-surface-2 focus-visible:bg-surface-2'

/**
 * Popover listing the viewer's communities first, then MG's other communities they can join (with a muted
 * "Join" hint). Platform admins also get a small "New community" item (REDESIGN section 1).
 */
export function CommunitySwitcher({
  currentSlug,
  children,
  className,
}: {
  currentSlug: string | null
  /** The community identity (icon and name); the whole block opens the switcher. */
  children?: ReactNode
  className?: string
}) {
  const { me, isPlatformAdmin } = useAuth()
  const [open, setOpen] = useState(false)
  const [opened, setOpened] = useState(false)
  const items = switcherCommunities(me?.communities)
  // MG's published communities, the same list as the home page (GET /api/discover/home), fetched when the
  // switcher first opens. Never the Discover ranking, which can hold other owners' communities (Q-FAM-07).
  const suggestions = useHomeCommunities(opened && !!me)
  const joinable = joinableCommunities(suggestions.data, me?.communities)
  const current = currentSlug?.toLowerCase()
  const close = () => setOpen(false)

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (next) setOpened(true)
      }}
    >
      <PopoverTrigger
        className={cn(
          'focus-ring group flex min-w-0 items-center gap-1.5 rounded-control py-1 pr-1 text-left transition-colors duration-150',
          className,
        )}
      >
        {children}
        <span className="sr-only">{t('Switch community')}</span>
        <span
          aria-hidden="true"
          className="grid size-8 shrink-0 place-items-center rounded-control text-subtle transition-colors duration-150 group-hover:bg-surface-2 group-hover:text-text group-data-[state=open]:bg-surface-2 group-data-[state=open]:text-text"
        >
          <ChevronsUpDown size={18} strokeWidth={1.75} />
        </span>
      </PopoverTrigger>
      <PopoverContent align="start" width={320} padding="sm" className="max-h-[min(70vh,560px)] overflow-y-auto">
        {items.length > 0 ? (
          <nav aria-label={t('Your communities')} className="pb-1.5">
            <p className="px-2.5 pt-1.5 pb-1 text-micro text-subtle">{t('Your communities')}</p>
            <ul>
              {items.map((c) => {
                const active = c.slug.toLowerCase() === current
                return (
                  <li key={c.id}>
                    <Link to={`/${c.slug}`} onClick={close} aria-current={active ? 'page' : undefined} className={rowClass}>
                      <CommunityIcon name={c.name} src={c.iconUrl} id={c.id} brandColor={c.brandColor} size={32} />
                      <span className="min-w-0 flex-1 truncate font-semibold">{c.name}</span>
                      {c.status === 'pending' ? <Badge tone="gray">{t('Requested')}</Badge> : null}
                      {active ? <Check size={16} strokeWidth={2} className="shrink-0 text-text" aria-hidden="true" /> : null}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </nav>
        ) : null}
        {joinable.length > 0 ? (
          <nav aria-label={t('More MG AI communities')} className={cn('pb-1.5', items.length > 0 && 'border-t border-line pt-1.5')}>
            <p className="px-2.5 pt-1.5 pb-1 text-micro text-subtle">{t('More MG AI communities')}</p>
            <ul>
              {joinable.map((c) => (
                <li key={c.id}>
                  <Link to={`/${c.slug}/about`} onClick={close} className={rowClass}>
                    <CommunityIcon name={c.name} src={c.iconUrl} id={c.id} brandColor={c.brandColor} size={32} />
                    <span className="min-w-0 flex-1 truncate font-semibold">{c.name}</span>
                    <span className="shrink-0 text-meta text-subtle">{t('Join')}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}
        {isPlatformAdmin ? (
          <div className={cn((items.length > 0 || joinable.length > 0) && 'border-t border-line pt-1.5')}>
            <Link to="/create" onClick={close} className={cn(rowClass, 'h-10 text-meta text-muted')}>
              <span className="grid size-8 place-items-center rounded-control bg-surface-2 text-subtle" aria-hidden="true">
                <Plus size={16} strokeWidth={1.75} />
              </span>
              {t('New community')}
            </Link>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}
